import { effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { DatiqGraphqlApolloService } from '@dat/datiq-graphql';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';
import { gql } from 'apollo-angular';
import { isEmpty } from 'lodash';
import { mergeLeft, path, zipWith } from 'ramda';
import { firstValueFrom } from 'rxjs';
import { EquipmentTypes, GetRatesNationalReponse, GetRatesNationalReponseSchema, GetRatesNationalResponseRate, Rate, RateInfo, RatesCache } from '../../models';

@Injectable({
    providedIn: 'root',
})
export class IqNtlGraphRatesStateService {
    private _apollo: DatiqGraphqlApolloService = inject(DatiqGraphqlApolloService);
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);

    rates: WritableSignal<RatesCache> = signal({
        [EquipmentTypes.Van]: {
            errored: false,
            rates: []
        },
        [EquipmentTypes.Flatbed]: {
            errored: false,
            rates: []
        },
        [EquipmentTypes.Reefer]: {
            errored: false,
            rates: []
        },
    });

    constructor() {
        this._bus.registerMap({
            'type:rate-fetch:national-rates': {
                callback: ({ equipmentType }: { equipmentType: EquipmentTypes }) => {
                    const isFetched = !isEmpty(this.rates()[equipmentType].rates);

                    if (isFetched) return;

                    this.fetchRates(equipmentType).then((rateInfo: RateInfo) => {
                        this.rates.update(currentRates => ({
                            ...currentRates,
                            [equipmentType]: rateInfo
                        }));
                    });
                }
            }
        });
    }

    private fetchRates(equipmentType: EquipmentTypes): Promise<RateInfo> {
        const contractRates = this._getRates('Contract', equipmentType);
        const spotRates = this._getRates('Spot', equipmentType);
        const fuelRates = this._getRates('Fuel', equipmentType);

        return Promise.all([
            contractRates,
            spotRates,
            fuelRates
        ]).then(([_contractRates, _spotRates, _fuelRates]) => {
            if (
                _contractRates.errored ||
                _spotRates.errored || 
                _fuelRates.errored
            ) {
                return {
                    errored: true,
                    rates: []
                };
            }

            const zipFn = (rate: GetRatesNationalResponseRate, fuel: GetRatesNationalResponseRate) => ({
                date: rate.date,
                withoutFuel: rate.average,
                withFuel: rate.average + fuel.average
            });

            const contract = zipWith(zipFn, _contractRates.rates, _fuelRates.rates).map(
                ({ date, withoutFuel, withFuel }) => ({
                    date,
                    contractWithFuel: withFuel,
                    contractWithoutFuel: withoutFuel
                })
            );
            const spot = zipWith(zipFn, _spotRates.rates, _fuelRates.rates).map(
                ({ date, withoutFuel, withFuel }) => ({
                    date,
                    spotWithFuel: withFuel,
                    spotWithoutFuel: withoutFuel
                })
            );

            return {
                errored: false,
                rates: zipWith(mergeLeft, contract, spot)
            };
        });
    }

    private _getRates(
        rateType: 'Spot' | 'Contract' | 'Fuel',
        equipmentType: EquipmentTypes
    ): Promise<{ errored: boolean; rates: GetRatesNationalResponseRate[] }> {
        return firstValueFrom(
            this._apollo.getApollo()
                .query({
                    query: gql`
                        query GetRatesNational ($query: RATESNATIONAL_SearchCriteriaInputRatesNational) {
                            response: getRatesNational(query: $query) {
                                rates {
                                    mile {
                                        date
                                        average
                                    }
                                }
                            }
                        }
                    `,
                    variables: {
                        query: {
                            equipmentCategory: equipmentType,
                            period: 'Month',
                            country: 'US',
                            rateType
                        }
                    }
                })
        ).then((result: ApolloQueryResult<GetRatesNationalReponse>) => this._processResult(result));
    }

    private _processResult(
        result: ApolloQueryResult<GetRatesNationalReponse>,
    ): { errored: boolean; rates: GetRatesNationalResponseRate[] } {
        try {
            const data: GetRatesNationalReponse = GetRatesNationalReponseSchema.parse(result.data);
            const rates: GetRatesNationalResponseRate[] = path(['response', 'rates', 'mile'], data);
    
            return {
                errored: false,
                rates
            };
        } catch (err) {
            return {
                errored: true,
                rates: []
            }
        }
    }

}
